import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import type { Recording, RecordingPatch } from "../api";
import { maxTitle } from "./EditTitle";
import { dateOnly, t, phrases } from "../i18n";
import { useModalKeys } from "./Modal";

// A recording's own edit (byj-1wv.38, byj-1wv.43): its name, and how long it
// is kept — days from when it was recorded, up to the relay's maximum. A
// class's recording keeps the class's retention, which its students were
// told when they joined, so its dialog says so in place of the choice. Only
// what changed is sent; the relay checks the rest.

const choices = [7, 30, 90, 365];
const day = 86_400_000;

type Editing = { rec: Recording; resolve: (patch: RecordingPatch | null) => void };

/**
 * `[edit, dialog]`: `edit(rec)` shows the recording's name and retention and
 * resolves to what was changed, or null when nothing was saved.
 */
export function useEditRecording(): [(rec: Recording) => Promise<RecordingPatch | null>, ReactNode] {
  const [editing, setEditing] = useState<Editing | null>(null);
  const edit = useCallback((rec: Recording) => new Promise<RecordingPatch | null>((resolve) => setEditing({ rec, resolve })), []);
  const done = useCallback(
    (patch: RecordingPatch | null) => {
      editing?.resolve(patch);
      setEditing(null);
    },
    [editing],
  );
  return [edit, editing ? <EditRecordingDialog rec={editing.rec} onDone={done} /> : null];
}

function EditRecordingDialog({ rec, onDone }: { rec: Recording; onDone: (patch: RecordingPatch | null) => void }) {
  const [title, setTitle] = useState(rec.title || "");
  const now = rec.retention_days ?? 30;
  const [days, setDays] = useState(now);
  const input = useRef<HTMLInputElement>(null);
  const recorded = new Date(rec.created_at).getTime();
  const until = (d: number) => new Date(recorded + d * day);
  const max = rec.max_retention_days ?? 0;
  // the choices it may take: within the maximum, and kept to a day not yet
  // past; the one it has now, whatever it is, stays on the list
  const offered = [...new Set([...choices.filter((d) => d <= max), now])].sort((a, b) => a - b);
  const name = title.trim();
  const patch: RecordingPatch = {};
  if (name !== "" && name !== (rec.title || "").trim()) patch.title = name;
  if (!rec.class_retention && days !== now) patch.retention_days = days;
  const changed = Object.keys(patch).length > 0;

  // Esc closes it alone, and Tab stays in it (components/Modal)
  const keys = useModalKeys(() => onDone(null));
  useEffect(() => {
    input.current?.focus();
    input.current?.select();
  }, []);

  return (
    <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && onDone(null)}>
      <form
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-recording-heading"
        onKeyDown={keys}
        onSubmit={(e) => {
          e.preventDefault();
          if (changed) onDone(patch);
        }}
      >
        <h3 id="edit-recording-heading">{t("recordings:editRecordingHeading")}</h3>
        <label className="field">
          {t("recordings:titleLabel")}
          <input ref={input} value={title} maxLength={maxTitle} onChange={(e) => setTitle(e.target.value)} />
        </label>
        {rec.class_retention ? (
          <p className="muted small">{t("recordings:classRetentionNote", { days: now })}</p>
        ) : max > 0 ? (
          <>
            <label className="field keep-for">
              {t("recordings:keepForLabel")}
              <select value={days} onChange={(e) => setDays(Number(e.target.value))}>
                {offered.map((d) => (
                  <option key={d} value={d} disabled={d !== now && until(d).getTime() <= Date.now()}>
                    {phrases.retentionChoice(d)}
                  </option>
                ))}
              </select>
            </label>
            <p className="muted small keep-until">{t("recordings:retentionUntil", { when: dateOnly(until(days)) })}</p>
          </>
        ) : null}
        <div className="row modal-actions">
          <button type="button" onClick={() => onDone(null)}>
            {t("common:cancel")}
          </button>
          <button type="submit" className="primary" disabled={!changed}>
            {t("common:save")}
          </button>
        </div>
      </form>
    </div>
  );
}
